import {
  fsmDefFormSuccessContentSchema,
  fsmDefFormSuccessTriggerSchema,
  type BlockFormSuccess,
  type FsmDefFormSuccess,
  type FsmDefFormSuccessContent,
  type FsmDefFormSuccessTrigger,
} from "@do/shared-types";
import { cn } from "@do/ui/lib/utils";
import { CheckIcon } from "lucide-react";

export type FormSuccessFieldRecap = {
  id: string;
  label: string;
  value: string;
};

export type MessageStepFormSuccessProps = {
  className?: string;
  id?: string;
  content: Pick<BlockFormSuccess, "title" | "text" | "recapitulatif" | "followingSteps">;
};

export const getFormSuccessTrigger = (success?: FsmDefFormSuccess): FsmDefFormSuccessTrigger => {
  const parsed = fsmDefFormSuccessTriggerSchema.safeParse(success?.trigger);
  return parsed.success ? parsed.data : "onSubmit";
};

export const resolveFormSuccessContent = (
  success: FsmDefFormSuccess | undefined,
  localeKey: string,
  defaultLocaleKey: string
): FsmDefFormSuccessContent | null => {
  if (!success) return null;

  const candidates = [localeKey, defaultLocaleKey, ...Object.keys(success)];
  for (const key of candidates) {
    if (key === "trigger") continue;
    const parsed = fsmDefFormSuccessContentSchema.safeParse(success[key]);
    if (parsed.success) return parsed.data;
  }

  return null;
};

export type FormSuccessBooleanLabels = {
  true: string;
  false: string;
};

/** Formats a form field value for the success recap (localized booleans). */
export const formatFormSuccessRecapValue = (
  rawValue: unknown,
  booleanLabels: FormSuccessBooleanLabels
): string => {
  if (typeof rawValue === "boolean") {
    return rawValue ? booleanLabels.true : booleanLabels.false;
  }
  return String(rawValue ?? "").trim();
};

/**
 * Form submit success message rendered in the conversation.
 * Design this component freely — props are stable.
 */
export const MessageStepFormSuccess = ({ className, id, content }: MessageStepFormSuccessProps) => {
  const recapFields = content.recapitulatif?.fields ?? [];
  const showRecap = Boolean(content.recapitulatif) && recapFields.length > 0;
  const followingSteps = content.followingSteps?.steps ?? [];

  return (
    <div
      className={cn(
        className,
        "flex w-full flex-col",
        "max-w-(--message-form-success-max-width)",
        "gap-(--message-form-success-gap)",
        "p-(--message-form-success-padding)",
        "rounded-(--message-form-success-border-radius)",
        "bg-(--message-form-success-background)/(--message-form-success-background-opacity)"
      )}
      id={id ? `${id}-success` : undefined}
      data-testid="form-success"
    >
      <div className={cn("flex items-center", "gap-(--message-form-success-header-gap)")}>
        <div
          className={cn(
            "flex shrink-0 items-center justify-center rounded-full",
            "size-(--message-form-success-icon-size)",
            "bg-(--message-form-success-icon-background)/(--message-form-success-icon-background-opacity)",
            "text-(--message-form-success-icon-color)/(--message-form-success-icon-color-opacity)"
          )}
        >
          <CheckIcon
            className={cn(
              "size-(--message-form-success-icon-inner-size)",
              "stroke-(length:--message-form-success-icon-stroke-width)"
            )}
          />
        </div>
        <div className="flex min-w-0 flex-col">
          <p
            className={cn(
              "text-(length:--message-form-success-title-font-size)",
              "font-(--message-form-success-title-font-weight)!",
              "font-(family-name:--message-form-success-title-font-family)",
              "leading-(--message-form-success-title-line-height)",
              "text-(--message-form-success-title-color)/(--message-form-success-title-color-opacity)"
            )}
          >
            {content.title}
          </p>
          <p
            className={cn(
              "text-(length:--message-form-success-text-font-size)",
              "font-(--message-form-success-text-font-weight)!",
              "font-(family-name:--message-form-success-text-font-family)",
              "leading-(--message-form-success-text-line-height)",
              "text-(--message-form-success-text-color)/(--message-form-success-text-color-opacity)"
            )}
          >
            {content.text}
          </p>
        </div>
      </div>

      {(showRecap || followingSteps.length > 0) && (
        <div
          className={cn(
            "flex w-full flex-col",
            "rounded-(--message-form-success-sections-border-radius)",
            "bg-(--message-form-success-sections-background)/(--message-form-success-sections-background-opacity)",
            "divide-y-(length:--message-form-success-sections-divide-width)",
            "divide-(--message-form-success-sections-divide-color)/(--message-form-success-sections-divide-opacity)"
          )}
        >
          {showRecap && (
            <div
              className={cn(
                "flex flex-col",
                "gap-(--message-form-success-section-gap)",
                "p-(--message-form-success-section-padding)"
              )}
            >
              {content.recapitulatif?.label && (
                <p
                  className={cn(
                    "text-(length:--message-form-success-section-label-font-size)",
                    "font-(--message-form-success-section-label-font-weight)!",
                    "font-(family-name:--message-form-success-section-label-font-family)",
                    "leading-(--message-form-success-section-label-line-height)",
                    "text-(--message-form-success-section-label-color)/(--message-form-success-section-label-color-opacity)"
                  )}
                >
                  {content.recapitulatif.label}
                </p>
              )}
              <ul
                className={cn(
                  "m-0 flex list-none flex-col p-0",
                  "gap-(--message-form-success-recap-gap)"
                )}
              >
                {recapFields.map((field) => (
                  <li
                    key={field.id}
                    className={cn("flex flex-col", "gap-(--message-form-success-recap-field-gap)")}
                  >
                    <span
                      className={cn(
                        "min-w-0",
                        "text-(length:--message-form-success-recap-field-label-font-size)",
                        "font-(--message-form-success-recap-field-label-font-weight)!",
                        "font-(family-name:--message-form-success-recap-field-label-font-family)",
                        "leading-(--message-form-success-recap-field-label-line-height)",
                        "text-(--message-form-success-recap-field-label-color)/(--message-form-success-recap-field-label-color-opacity)"
                      )}
                    >
                      {field.label}
                    </span>
                    <span
                      className={cn(
                        "flex min-w-0 flex-row",
                        "gap-(--message-form-success-recap-field-value-gap)"
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={cn(
                          "block h-auto rounded-(--message-form-success-recap-field-accent-border-radius)",
                          "w-(--message-form-success-recap-field-accent-width)",
                          "bg-(--message-form-success-recap-field-accent-color)/(--message-form-success-recap-field-accent-opacity)"
                        )}
                      />
                      <span
                        className={cn(
                          "text-(length:--message-form-success-recap-field-value-font-size)",
                          "font-(--message-form-success-recap-field-value-font-weight)!",
                          "font-(family-name:--message-form-success-recap-field-value-font-family)",
                          "leading-(--message-form-success-recap-field-value-line-height)",
                          "text-(--message-form-success-recap-field-value-color)/(--message-form-success-recap-field-value-color-opacity)"
                        )}
                        style={{
                          fontStyle: "var(--message-form-success-recap-field-value-font-style)",
                        }}
                      >
                        {field.value}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {followingSteps.length > 0 && (
            <div
              className={cn(
                "flex flex-col",
                "gap-(--message-form-success-section-gap)",
                "p-(--message-form-success-section-padding)"
              )}
            >
              {content.followingSteps?.label && (
                <p
                  className={cn(
                    "text-(length:--message-form-success-section-label-font-size)",
                    "font-(--message-form-success-section-label-font-weight)!",
                    "font-(family-name:--message-form-success-section-label-font-family)",
                    "leading-(--message-form-success-section-label-line-height)",
                    "text-(--message-form-success-section-label-color)/(--message-form-success-section-label-color-opacity)"
                  )}
                >
                  {content.followingSteps.label}
                </p>
              )}
              <ol
                className={cn(
                  "m-0 flex list-none flex-col p-0",
                  "gap-(--message-form-success-steps-gap)"
                )}
              >
                {followingSteps.map((step, index) => (
                  <li
                    key={`${index}-${step}`}
                    className={cn(
                      "flex items-start",
                      "gap-(--message-form-success-steps-item-gap)"
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        "flex shrink-0 items-center justify-center rounded-full",
                        "size-(--message-form-success-steps-badge-size)",
                        "bg-(--message-form-success-steps-badge-background)/(--message-form-success-steps-badge-background-opacity)",
                        "text-(--message-form-success-steps-badge-color)/(--message-form-success-steps-badge-color-opacity)",
                        "text-(length:--message-form-success-steps-badge-font-size)",
                        "font-(--message-form-success-steps-badge-font-weight)!",
                        "font-(family-name:--message-form-success-steps-badge-font-family)",
                        "leading-(--message-form-success-steps-badge-line-height)"
                      )}
                    >
                      {index + 1}
                    </span>
                    <span
                      className={cn(
                        "min-w-0",
                        "text-(length:--message-form-success-steps-text-font-size)",
                        "font-(--message-form-success-steps-text-font-weight)!",
                        "font-(family-name:--message-form-success-steps-text-font-family)",
                        "leading-(--message-form-success-steps-text-line-height)",
                        "text-(--message-form-success-steps-text-color)/(--message-form-success-steps-text-color-opacity)"
                      )}
                    >
                      {step}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
